import AddIcon from "@mui/icons-material/Add";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import ListItemIcon from "@mui/material/ListItemIcon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { styled } from "@mui/material/styles";
import LoadingButton from "_shared/_views/loading/loading-button";
import { useState, type MouseEvent as ReactMouseEvent, type ReactNode } from "react";

/**
 * One "add something to this collection" affordance — "Add Member", "Add Existing Category",
 * "From Marketplace Team", "Upload Policy Document".
 *
 * `disabled` offers the affordance greyed out rather than withheld, for the cases where it is the
 * viewer's but cannot be taken right now: an add whose caller has no setter to hand the selection to,
 * or a document upload, which the server refuses until the proposal itself has been saved. `loading`
 * is for that same upload while it is in flight, and `icon` for an add whose mark is not the plus — a
 * marketplace import and a template copy each carry their own.
 */
export type TableAddAction = {
  label: string;
  onClick: () => void;
  icon?: ReactNode;
  loading?: boolean;
  disabled?: boolean;
};

/**
 * The accessible name of the split button's dropdown, and the label a test or a screen reader reads
 * before the alternatives open. Exported because the browser harness opens the menu by this name.
 */
export const MORE_WAYS_TO_ADD_LABEL = "More ways to add";

/** The same empty list on every render, for a collection that offers nothing to add. */
const NO_ADD_ACTIONS: readonly TableAddAction[] = [];

/**
 * How a toolbar addresses this control in its own styles, without reaching for an Emotion class name
 * or for whichever MUI root a one-way or many-way add happens to render. `CollectionToolbar` uses it
 * to decide which piece of a wrapping row gives way first.
 */
export const TABLE_ADD_ACTIONS_ATTRIBUTE = "data-table-add-actions";

/**
 * Keeps the group from being stretched by a wrapping toolbar row and from giving way to the search
 * field beside it: the labels are already as short as they read.
 */
const AddActionsGroup = styled(ButtonGroup)(() => ({
  flexShrink: 0,
  width: "fit-content",
}));

const AddActionsSingle = styled(LoadingButton)(() => ({
  flexShrink: 0,
  width: "fit-content",
}));

export type TableAddActionsProps = {
  /**
   * The ways this collection may be added to, in the order they are read. The FIRST is the split
   * button's own press; the rest are the alternatives behind its dropdown.
   */
  actions?: readonly TableAddAction[];
};

/**
 * A collection's add affordances, as one control however many there are.
 *
 * One way to add is one ordinary button, exactly as it has always been drawn. More than one is MUI's
 * split button — the first action on the button itself, the rest on a dropdown beside it — rather than
 * a row of equal buttons, which is what the Fee Schedule and Vendor Team tabs had grown and what made
 * their toolbars wider than the panes they sit in (#4955).
 *
 * The dropdown IS a modal surface, and that is the point of it: it opens because the reader pressed
 * it, the same way MRT's own column-actions menus do, and it gives the page back on dismissal. What
 * #4895 forbade — and what the roster and collection specs still guard — is a surface that opens with
 * nobody having asked, which this never does.
 */
export default function TableAddActions(props: Readonly<TableAddActionsProps>) {
  const actions = props.actions ?? NO_ADD_ACTIONS;
  // The toggle itself is the anchor, held in state rather than in a ref: a ref read during the render
  // to position a portal is what `react-hooks/refs` reports, and an eslint-disable for any
  // `react-hooks/*` rule costs the whole component its React Compiler memoization (see app/CLAUDE.md).
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const menuOpen = Boolean(menuAnchor);

  const primary = actions[0];
  const alternatives = actions.slice(1);

  if (!primary) return null;

  if (alternatives.length === 0) {
    return (
      <AddActionsSingle
        data-table-add-actions=""
        startIcon={primary.icon ?? <AddIcon />}
        variant="outlined"
        size="small"
        loading={primary.loading ?? false}
        disabled={primary.disabled ?? false}
        onClick={primary.onClick}
      >
        {primary.label}
      </AddActionsSingle>
    );
  }

  return (
    <>
      <AddActionsGroup data-table-add-actions="" variant="outlined" size="small">
        <LoadingButton
          startIcon={primary.icon ?? <AddIcon />}
          loading={primary.loading ?? false}
          disabled={primary.disabled ?? false}
          onClick={primary.onClick}
        >
          {primary.label}
        </LoadingButton>
        <Button
          aria-label={MORE_WAYS_TO_ADD_LABEL}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          size="small"
          onClick={(event: ReactMouseEvent<HTMLButtonElement>) => setMenuAnchor(event.currentTarget)}
        >
          <ArrowDropDownIcon />
        </Button>
      </AddActionsGroup>
      <Menu
        anchorEl={menuAnchor}
        open={menuOpen}
        onClose={() => setMenuAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {alternatives.map((action) => (
          <MenuItem
            key={action.label}
            disabled={action.disabled ?? false}
            onClick={() => {
              setMenuAnchor(null);
              action.onClick();
            }}
          >
            {/* The mark an alternative carries is its own — a folder for an existing category, a copy
                for a template — and a plain add keeps the plus the single button would have shown. */}
            <ListItemIcon>{action.icon ?? <AddIcon fontSize="small" />}</ListItemIcon>
            {action.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
